/**
 * TimeFilterContext — global time/date filter state shared across all screens.
 *
 * Replaces three separate filter patterns (DateRangeFilter, PeriodSelector,
 * ReportShell week dropdown) with one canonical selection that persists
 * across navigation. Every screen reads from `useTimeFilter()` and converts
 * the selection to the right API parameters via helper functions.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api, type DateRange, type PeriodQueueItem, type PeriodQueueResponse } from '../api';

// ── Types ────────────────────────────────────────────────────────────────

export type Preset = 'all' | 'week' | 'month' | 'quarter' | 'ytd' | 'lastQuarter';

export type TimeSelection =
  | { mode: 'all' }
  | { mode: 'range'; preset: Preset | 'custom'; from: string; to: string }
  | { mode: 'period'; periodId: string; weekEnding: string; periodStart: string };

export interface TimeFilterState {
  readonly selection: TimeSelection;
  readonly periodQueue: PeriodQueueResponse | null;
  readonly periodQueueLoading: boolean;

  /** Change the active filter. */
  select: (selection: TimeSelection) => void;
  /** Select a preset range. */
  selectPreset: (preset: Preset) => void;
  /** Select a specific pay period by ID. */
  selectPeriod: (periodId: string) => void;
  /** Select a custom date range. */
  selectCustomRange: (from: string, to: string) => void;
  /** Re-fetch the period queue (e.g. after a pipeline action updates progress). */
  reloadPeriodQueue: () => void;

  /** Convert current selection to a DateRange (from/to) for API calls. */
  toDateRange: () => DateRange | undefined;
  /** Convert current selection to a periodId for API calls. */
  toPeriodId: () => string | undefined;
  /** Convert current selection to a weekEnding string for API calls. */
  toWeekEnding: () => string | undefined;
  /** Display label for the current selection. */
  displayLabel: string;
}

// ── Preset computation ───────────────────────────────────────────────────

function computePreset(preset: Preset): DateRange | undefined {
  if (preset === 'all') return undefined;
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = today.getMonth();
  const dd = today.getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
  const todayIso = iso(yyyy, mm, dd);

  switch (preset) {
    case 'week': {
      const start = new Date(yyyy, mm, dd - 6);
      return { from: iso(start.getFullYear(), start.getMonth(), start.getDate()), to: todayIso };
    }
    case 'month':
      return { from: iso(yyyy, mm, 1), to: todayIso };
    case 'quarter': {
      const qStart = new Date(yyyy, Math.floor(mm / 3) * 3, 1);
      return {
        from: iso(qStart.getFullYear(), qStart.getMonth(), qStart.getDate()),
        to: todayIso,
      };
    }
    case 'ytd':
      return { from: iso(yyyy, 0, 1), to: todayIso };
    case 'lastQuarter': {
      const qStart = new Date(yyyy, Math.floor(mm / 3) * 3 - 3, 1);
      const qEnd = new Date(yyyy, Math.floor(mm / 3) * 3, 0);
      return {
        from: iso(qStart.getFullYear(), qStart.getMonth(), qStart.getDate()),
        to: iso(qEnd.getFullYear(), qEnd.getMonth(), qEnd.getDate()),
      };
    }
  }
}

export const PRESET_LABELS: Record<Preset | 'custom', string> = {
  all: 'All time',
  week: 'This week',
  month: 'This month',
  quarter: 'This quarter',
  ytd: 'Year to date',
  lastQuarter: 'Last quarter',
  custom: 'Custom range',
};

// ── Default selection ────────────────────────────────────────────────────

function defaultSelection(): TimeSelection {
  const range = computePreset('ytd');
  if (!range) return { mode: 'all' };
  return { mode: 'range', preset: 'ytd', from: range.from, to: range.to };
}

// ── Context ──────────────────────────────────────────────────────────────

const TimeFilterCtx = createContext<TimeFilterState | null>(null);

export function useTimeFilter(): TimeFilterState {
  const ctx = useContext(TimeFilterCtx);
  if (ctx === null) throw new Error('useTimeFilter must be used within <TimeFilterProvider>');
  return ctx;
}

// ── Provider ─────────────────────────────────────────────────────────────

export function TimeFilterProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<TimeSelection>(defaultSelection);
  const [periodQueue, setPeriodQueue] = useState<PeriodQueueResponse | null>(null);
  const [periodQueueLoading, setPeriodQueueLoading] = useState(true);

  const fetchQueue = useCallback(() => {
    setPeriodQueueLoading(true);
    api
      .periodQueue()
      .then((data) => {
        setPeriodQueue(data);
        setPeriodQueueLoading(false);
      })
      .catch(() => {
        setPeriodQueueLoading(false);
      });
  }, []);

  // Load period queue on mount
  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const value = useMemo((): TimeFilterState => {
    const findPeriod = (id: string): PeriodQueueItem | undefined => {
      if (!periodQueue) return undefined;
      return [...periodQueue.activePeriods, ...periodQueue.recentHistory].find((p) => p.id === id);
    };

    const selectPreset = (preset: Preset) => {
      if (preset === 'all') {
        setSelection({ mode: 'all' });
      } else {
        const range = computePreset(preset);
        if (range) setSelection({ mode: 'range', preset, from: range.from, to: range.to });
      }
    };

    const selectPeriod = (periodId: string) => {
      const period = findPeriod(periodId);
      if (period) {
        setSelection({
          mode: 'period',
          periodId: period.id,
          weekEnding: period.weekEnding,
          periodStart: period.periodStart,
        });
      }
    };

    const selectCustomRange = (from: string, to: string) => {
      setSelection({ mode: 'range', preset: 'custom', from, to });
    };

    const toDateRange = (): DateRange | undefined => {
      switch (selection.mode) {
        case 'all':
          return undefined;
        case 'range':
          return { from: selection.from, to: selection.to };
        case 'period':
          return { from: selection.periodStart, to: selection.weekEnding };
      }
    };

    const toPeriodId = (): string | undefined => {
      if (selection.mode === 'period') return selection.periodId;
      return undefined;
    };

    const toWeekEnding = (): string | undefined => {
      switch (selection.mode) {
        case 'all':
          return undefined;
        case 'range':
          return selection.to;
        case 'period':
          return selection.weekEnding;
      }
    };

    let displayLabel: string;
    switch (selection.mode) {
      case 'all':
        displayLabel = 'All time';
        break;
      case 'range':
        displayLabel =
          selection.preset === 'custom'
            ? `${selection.from} – ${selection.to}`
            : PRESET_LABELS[selection.preset];
        break;
      case 'period':
        displayLabel = `Week ending ${selection.weekEnding}`;
        break;
    }

    return {
      selection,
      periodQueue,
      periodQueueLoading,
      select: setSelection,
      selectPreset,
      selectPeriod,
      selectCustomRange,
      reloadPeriodQueue: fetchQueue,
      toDateRange,
      toPeriodId,
      toWeekEnding,
      displayLabel,
    };
  }, [selection, periodQueue, periodQueueLoading, fetchQueue]);

  return <TimeFilterCtx.Provider value={value}>{children}</TimeFilterCtx.Provider>;
}
